import {useContext} from "react";
import URI from 'urijs';
import moment from 'moment';

import {StoreContext} from "../../components/stores/StoreLoader";
import JSONWebToken from "../../components/utils/JSONWebToken";
import {PageHead} from "../src/react/components/PageHead";
import ErrorPage from "../../components/pages/ErrorPage";
import {SchoolBlocksPageWrapper} from "../../components/pages/SchoolBlocksPageWrapper";
import AdminSidebar from "../../components/components/admin/sidebar/AdminSidebar";
import {observer} from "mobx-react";
import SafeRouter from "../../components/components/utilities/SafeRouter";
import WatsonApi from "../../components/backends/WatsonApi";
import {withLocaleInURI} from "../../components/internationalization/i18nURI";
import {getCookieFromString, getValidCookieDomain} from "../../components/utils/StringUtilities";
import LanguageTool from "../../components/components/header/LanguageTool/LanguageTool";
import {HTML5Backend} from "react-dnd-html5-backend";
import dynamic from "next/dynamic";
import Swagger from 'swagger-client';

const DndProvider = dynamic(() => import("react-dnd").then(module => module.DndProvider));


function DragAndDropProvider(props) {
    const {userStore} = useContext(StoreContext);
    if (userStore.isEditor) {
        return <DndProvider backend={HTML5Backend}>
            {props.children}
        </DndProvider>
    } else {
        return props.children;
    }
}

const GridPage = observer((props) => {
    const {userStore} = useContext(StoreContext);
    try {
        SafeRouter.setQueries(["token"], ["context"]);

        const jwt = new JSONWebToken();
        const token = jwt.decode(props.jwt);
        userStore.updateUser(token.user);

        return <DragAndDropProvider>
            <PageHead currentFullUrl={props.currentFullUrl} currentTitle={props.currentOrganization.title} />
            <LanguageTool />
            <SchoolBlocksPageWrapper {...props} />
            <AdminSidebar/>
        </DragAndDropProvider>
    } catch (error) {
        return <ErrorPage/>
    }
});

const isServerReq = req => !req.url.startsWith('/_next');

export async function getServerSideProps(ctx) {
    const {req, resolvedUrl, locale, res} = ctx;
    const skipLookupPaths = ['/service-worker.js', '/manifest.webmanifest', '/favicon.ico'];
    const resolveParentPaths = ['/robots.txt', '/sitemap.xml', '/manifest.webmanifest'];
    const currentBaseUrl = req.headers['x-forwarded-proto'] + "://" + req.headers.host.replace(/\/$/, "");

    const returnProps = {
        currentBaseUrl,
        locale,
        userAgent: req.headers['user-agent'],
    }

    const parsedUri = URI(resolvedUrl);
    const isServerSideRequest = isServerReq(req);
    const fullPath = parsedUri.removeQuery("all");
    const resource = fullPath.resource() || "/";
    const path = fullPath.pathname();
    const parsedQuery = parsedUri.search(true);

    const currentFullUrl = currentBaseUrl + path;
    let token;

    if (parsedUri.hasQuery("token")) {
        const fullParsedUri = URI(currentBaseUrl);
        const domain = getValidCookieDomain(fullParsedUri.hostname());

        const decoded = new JSONWebToken().decode(parsedQuery.token);
        token = parsedQuery.token;
        const validExpiration = moment.unix(decoded.exp).format("ddd, DD MMM YYYY HH:mm:ss ZZ");

        res.setHeader("Set-Cookie", [
            `token=${token}; Secure; Path=/; Expires=${validExpiration}; Domain=${domain}`,
            `context=${parsedQuery.context}; Secure; Expires=${validExpiration}; HttpOnly; Domain=${domain}`,
        ]);
    }

    if (!token) {
        token = getCookieFromString("token", req.headers.cookie);
    }

    try {
        if (!skipLookupPaths.includes(resource) && typeof req !== "undefined") {
            const responses = await Promise.all([
                new Promise(async (res, rej) => {
                    try {
                        const fetchUrl = encodeURI(`${currentBaseUrl}/lookup/?lookup_url=${withLocaleInURI(resource, locale)}`);

                        // TODO: remove conditional after some time has passed and confident folks have re-authenticated
                        let fetchOptions = {};
                        if (token) {
                            fetchOptions.headers = {
                                'Cookie': req.headers.cookie,
                            }
                        }

                        const response = await fetch(fetchUrl, fetchOptions);
                        const data = await response.json();
                        res(data);
                    } catch (e) {
                        rej(e);
                    }
                }),
                new Promise(async (res, rej) => {
                    try {
                        const client = await WatsonApi();

                        const expand = []

                        if (isServerSideRequest) {
                            expand.push(
                                "title_organization",
                                "title_organization.locations",
                                "title_organization.navigation",
                                "title_organization.categories",
                                "title_organization.media",
                                "title_organization.primary_hostname",
                                "title_organization.ancestor_tree",
                                "title_organization.ancestor_tree.navigation",
                                "title_organization.ancestor_tree.primary_hostname",
                            )
                        }

                        const clientAuthorizations = {};
                        if (token) {
                            clientAuthorizations.api_key = new Swagger.ApiKeyAuthorization('api-key', token, 'header');
                        }

                        const response = await client.lookups.lookups_list({
                            url: resolveParentPaths.includes(resource) ? currentBaseUrl : currentFullUrl,
                            expand,
                        }, {
                            clientAuthorizations
                        });
                        const resolveObject = {
                            currentOrganization: response.obj,
                            view: response.obj.view,
                            notifications: response.obj.alerts,
                        }

                        if (response.obj.blocks) {
                            resolveObject.blocks = response.obj.blocks;
                        }

                        if (isServerSideRequest) {
                            // on client-side transitions we don't need to replace title_organization
                            resolveObject.organization = response.obj.title_organization;
                        }

                        res(resolveObject);
                    } catch (e) {
                        rej(e);
                    }
                })
            ]);

            responses.forEach(r => Object.assign(returnProps, r));
        }

        return {
            notFound: false,
            props: {
                ...returnProps,
                view: resolvedUrl === "/_offline" ? "offline" : returnProps.view,
            },
        }

    } catch (error) {
        console.error(error);
        return {
            notFound: true,
        }
    }
}
export default GridPage;
